import { forEach } from 'jszip';
'use strict';
import { Component, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { debounceTime, switchMap, finalize } from "rxjs/operators";

import { NfseService } from '../shared/nfse.service';
import { CfopService } from '../../cfop/shared/cfop.service';
import { AliquotaImpostoService } from '../shared/aliquota-imposto.service';
import { AliquotaImpostoDto  } from '../shared/aliquota-imposto.model';
import { NbsService } from '../shared/nbs.service';
import { Nbs } from '../shared/nbs.model';

import { InfoDialogComponent } from '../../core/dialogs/info-dialog/info-dialog.component';
import { ConfirmDialogComponent } from '../../core/dialogs/confirm-dialog/confirm-dialog.component';

import { HtmlPrinterService } from '../../core/html-printer/html-printer.service';
import { ExportaXmlService } from '../../core/exporta-xml/exporta-xml.service';
import { UfService } from '../../uf/shared/uf.service';

import { NfseStatusComponent } from '../nfse-status/nfse-status.component';

import { Nfse } from '../shared/nfse.model';
import { MunicipioService } from 'src/app/municipio/shared/municipio.service';
import { Empresa } from 'src/app/empresa/shared/empresa.model';
import { TipoTemplate } from 'src/app/template/shared/template.model';



export interface DialogData {
  nfseId: number;
}

@Component({
  selector: 'nfse-cadastro',
  templateUrl: 'nfse-cadastro.component.html',
})

export class NfseCadastroComponent {

  empresas: any = [];
  listaServicos: any = [];
  statusNfse: string = '';
  nfseCadastroForm: FormGroup;
  maximizado: boolean;
  autorizada: boolean = false;
  processar: boolean = false;
  cancelar: boolean = false;
  clientes: any[];
  ufs: any = [];
  municipios: any = [];
  isDisabled: boolean = false;
  impostosNfseRetencao: any;
  cnae: string;
  possuiTemplate: boolean = false;
  aliquotasImposto: AliquotaImpostoDto[] = [];
  aliquotaImpostoSelecionada: AliquotaImpostoDto | null = null;
  carregandoAliquotasImposto: boolean = false;
  clienteSelecionado: any = null;
  tiposOperacaoCompraGovOuImoveis = [
    { valor: 0, descricao: 'Não se aplica' },
    { valor: 1, descricao: 'Fornecimento realizado com pagamento posterior' },
    { valor: 2, descricao: 'Recebimento do pagamento referente a fornecimento já realizado' },
    { valor: 3, descricao: 'Fornecimento realizado com pagamento já efetuado' },
    { valor: 4, descricao: 'Recebimento do pagamento antes da realização do fornecimento' }
  ];

  constructor(
    public dialog: MatDialog,
    private _snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<NfseCadastroComponent>,
    private _formBuilder: FormBuilder,
    private htmlPrinterService: HtmlPrinterService,
    private exportaXmlService: ExportaXmlService,
    @Inject(MAT_DIALOG_DATA) public INfse: DialogData,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private nfseService: NfseService,
    private cfopService: CfopService,
    private ufService: UfService,
    private municipioService: MunicipioService,
    private aliquotaImpostoService: AliquotaImpostoService) {
  }

  ngOnInit() {

    this.nfseCadastroForm = this._formBuilder.group(new Nfse());
    this.nfseCadastroForm.addControl('clienteAutoComplete', new FormControl(''));
    this.nfseCadastroForm.get('clienteAutoComplete')?.setValidators(Validators.required);
    this.nfseCadastroForm.get('discriminacao')?.setValidators(Validators.required);
    this.nfseCadastroForm.get('valorServicos')?.setValidators([Validators.required, Validators.min(0.01)]);
    this.nfseCadastroForm.get('listaServico')?.setValidators(Validators.required);
    this.configurarCamposCompraGovernamental();
   
    this.nfseCadastroForm.updateValueAndValidity();

    if (this.INfse.nfseId > 0) {
      this.buscaNfse(this.INfse.nfseId);

    } else {
      this.nfseCadastroForm.get('tipoRps').setValue(1);
    }

    this.buscarEmpresas();
    this.buscarUFs();

    const ufId = this.data?.ufId ?? 0;
    const municipioId = this.data?.municipioId ?? 0;

    if (ufId !== 0 && municipioId !== 0) {
      this.nfseCadastroForm.get('localPrestacaoUfId').setValue(ufId);
      this.nfseCadastroForm.get('localPrestacaoMunicipioId').setValue(municipioId);
    }
    this.onChanges();
    this.restaurarDialog();

    let valorIssAnterior = 0; // Valor anterior do ISS para manter controle

    // Monitora alterações no campo valorIss
    this.nfseCadastroForm.get('valorIss')?.valueChanges.subscribe((novoValorIss) => {
      const issRetido = this.nfseCadastroForm.get('issRetido')?.value;
      const valorLiquidoNfse = this.nfseCadastroForm.get('valorLiquidoNfse')?.value || 0;

      if (issRetido == 1) {
        // Se ISS está retido, subtrai o valor anterior e adiciona o novo valor
        const valorLiquidoAtualizado = valorLiquidoNfse + valorIssAnterior - novoValorIss;

        this.nfseCadastroForm.patchValue({
          valorLiquidoNfse: valorLiquidoAtualizado,
          valorIssRetido: novoValorIss // Atualiza valorIssRetido com o novo valor do valorIss
        });

        valorIssAnterior = novoValorIss; // Atualiza o valor anterior
      }
    });

    // Monitora alterações no campo issRetido
    this.nfseCadastroForm.get('issRetido')?.valueChanges.subscribe((issRetido) => {
      const valorIssAtual = this.nfseCadastroForm.get('valorIss')?.value || 0;
      const valorLiquidoNfse = this.nfseCadastroForm.get('valorLiquidoNfse')?.value || 0;

      if (issRetido === 2) {
        // Se ISS não está retido, adiciona o valor de valorIss ao valorLiquidoNfse
        const valorLiquidoAtualizado = valorLiquidoNfse + valorIssAtual;

        this.nfseCadastroForm.patchValue({
          valorIssRetido: 0, // Zera valorIssRetido
          valorLiquidoNfse: valorLiquidoAtualizado
        });

        valorIssAnterior = 0; // Zera o valor anterior para evitar duplicações
      }

      if (issRetido === 1) {
        // Se ISS está retido, subtrai o valor de valorIss do valorLiquidoNfse
        const valorLiquidoAtualizado = valorLiquidoNfse - valorIssAtual;

        this.nfseCadastroForm.patchValue({
          valorIssRetido: valorIssAtual, // Atualiza valorIssRetido com o valor de valorIss
          valorLiquidoNfse: valorLiquidoAtualizado
        });

        valorIssAnterior = valorIssAtual; // Atualiza o valor anterior para manter controle
      }
    });
  }



  fecharDialog() {
    this.dialogRef.close();
  }

  maximizarDialog() {
    this.dialogRef.updateSize('100%', '100%');
    this.maximizado = true;
  }

  restaurarDialog() {
    let innerWidth = window.innerWidth;

    if (innerWidth < 1024) {
      this.dialogRef.updateSize('90%', '100vh');
    }
    else {
      this.dialogRef.updateSize('70%', '100vh');
    }
    this.maximizado = false;
  }

  buscarUFs() {
    this.ufService.getUFs().subscribe(data => {
      this.ufs = data.body;
    });
  }

  buscarMunicipioPorUf(ufId: number) {
    this.municipioService.getMunicipioPorUf(ufId).subscribe(data => {
      this.municipios = data.body;
    });
  }

  buscarEmpresas() {
    this.nfseService.getEmpresa().subscribe(data => {
      this.empresas = data.body;
    });
  }

  buscaListaServico(empresaId) {
    this.nfseService.getListaServicoCadastrados(empresaId).subscribe(data => {
      this.listaServicos = data.body;
    });
  }

  onChanges(): void {
    this.nfseCadastroForm
      .get('clienteAutoComplete')
      .valueChanges
      .pipe(
        debounceTime(300),
        switchMap(value => value?.length >= 3 ?
          this.nfseService.getClientePorNome(value) : []
        )
      )
      .subscribe(data =>
        this.clientes = data.body
      );

    this.nfseCadastroForm
      .get('localPrestacaoUfId')
      .valueChanges
      .subscribe(data => this.buscarMunicipioPorUf(data));
  }

buscaNfse(nfseId: number): Promise<void> {
  return new Promise((resolve, reject) => {


      this.nfseService.getNfse(nfseId).subscribe({
        next: (data) => {
          this.insereDadosNfse(data.body);
          this.buscaListaServico(data.body.empresaId);
          this.atualizaStatus(this.nfseCadastroForm.value);
          this.buscarTemplates();
          resolve();
        },
        error: (error: HttpErrorResponse) => {
          this.dialog.open(InfoDialogComponent, {
            data: {
              titulo: "Erro",
              erro: error
            }
          });
          reject(error);
        }
      });
    });
  }


  onEmpresaChange(empresaId: number) {
    const empresa = this.empresas.find(x => x.empresaId === empresaId);

    if (empresa) {
      this.empresaSelecionada(empresa);
      return;
    }

    this.listaServicos = [];
    this.buscarAliquotasImpostoPorEmpresa(0, false);
  }

  empresaSelecionada(empresa: any) {

    if (
      this.nfseCadastroForm.get('localPrestacaoUfId').value == undefined ||
      this.nfseCadastroForm.get('localPrestacaoUfId').value == null ||
      this.nfseCadastroForm.get('localPrestacaoUfId').value == 0
    ) {
      this.nfseCadastroForm.get('localPrestacaoUfId').setValue(empresa.municipio.ufId);
    }
    if (
      this.nfseCadastroForm.get('localPrestacaoMunicipioId').value == undefined ||
      this.nfseCadastroForm.get('localPrestacaoMunicipioId').value == null ||
      this.nfseCadastroForm.get('localPrestacaoMunicipioId').value == 0
    ) {
      this.nfseCadastroForm.get('localPrestacaoMunicipioId').setValue(empresa.municipio.municipioId);
      this.nfseCadastroForm.get('codigoMunicipioIbge').setValue(empresa.municipio.codigoIBGE);
    }


    let ufId;
    let municipioId;
    let municipio;

    const intervalId = setInterval(() => {
      if (this.municipios && this.municipios.length > 0) {
        clearInterval(intervalId);

        ufId = this.data?.ufId ? 0 : empresa.municipio.ufId;
        municipioId = this.data?.municipioId ? 0 : empresa.municipio.municipioId;
        municipio = this.municipios.filter(municipio => municipio.municipioId == municipioId)[0];

        if (ufId !== 0 && municipioId !== 0) {
          this.nfseCadastroForm.get('localPrestacaoUfId').setValue(ufId);
          this.nfseCadastroForm.get('localPrestacaoMunicipioId').setValue(municipioId);
          this.nfseCadastroForm.get('codigoMunicipioIbge').setValue(municipio?.codigoIBGE);
        }

        this.buscaListaServico(empresa.empresaId);
      }
    }, 100);

    this.buscarAliquotasImpostoPorEmpresa(empresa.empresaId, false);
  }

  insereDadosNfse(data: any) {

    this.nfseCadastroForm.patchValue(data);

    // seta localPrestacaoUf e por caixa do onChanges() automaticamente também busca municipios para listar
    if (data.localPrestacaoMunicipio) {
      this.nfseCadastroForm
        .get('localPrestacaoUfId')
        ?.setValue(data.localPrestacaoMunicipio.ufId);
    }

    this.clienteSelecionado = data.cliente || null;
    this.nfseCadastroForm.get('clienteAutoComplete').setValue(data.cliente.nomeFantasia);
    this.atualizarValidacaoChaveNfseReferenciadaCompraGovOuImoveis();

    const municipioPrestacao = data?.localPrestacaoMunicipio ?? data?.municipio;
    const ufIdDoMunicipio = municipioPrestacao?.uf?.ufId ?? municipioPrestacao?.ufId ?? 0;
    const municipioIdDoMunicipio = data?.localPrestacaoMunicipioId ?? municipioPrestacao?.municipioId ?? 0;

    if (ufIdDoMunicipio > 0) {
      this.nfseCadastroForm.get('localPrestacaoUfId').setValue(ufIdDoMunicipio);
    }

    if (municipioIdDoMunicipio > 0) {
      this.nfseCadastroForm.get('localPrestacaoMunicipioId').setValue(municipioIdDoMunicipio);
    }


    const aliquotaImpostoId = data.aliquotaImpostoId || data.aliquotaImposto?.aliquotaImpostoId || 0;

    if (aliquotaImpostoId > 0) {
      this.nfseCadastroForm.patchValue({ aliquotaImpostoId });
    }

    if (data.empresaId > 0) {
      this.buscarAliquotasImpostoPorEmpresa(data.empresaId, true);
    }

    if (this.nfseCadastroForm.value.statusProcessamento == "100") {
      this.autorizada = true;
      this.isDisabled = true;
    };

    if (this.nfseCadastroForm.value.statusProcessamento != "95" && this.nfseCadastroForm.value.statusProcessamento != "135" && this.nfseCadastroForm.value.nProt != null) {
      this.cancelar = true;
    };

    const statusInvalidos = ["100", "101", "102", "135", "105", "2"];

    if (!statusInvalidos.includes(this.nfseCadastroForm.value.statusProcessamento)) {
      this.processar = true;
    }


    this.atualizaStatus(this.nfseCadastroForm.value);
  }

async salvar(): Promise<void> {
  if (this.nfseCadastroForm.value.nfseId == 0) {
    // NOVA ARQUITETURA - Usa endpoint /api/nfse/criar
    return await this.salvarNovaArquitetura();
  } else {
      // EDIÇÃO - converte apenas os campos que sao diferente do back
      return new Promise((resolve, reject) => {
        const { issRetido, tipoRps, ...rest } = this.nfseCadastroForm.value;

        const payload = {
          ...rest,
          isIssqnRetido: issRetido === 1,
          tipoDps: tipoRps || 1,
          nfseItens: [{
            descricao: rest.discriminacao,
            quantidade: 1,
            valorUnitario: rest.valorServicos
          }]
        };

        this.nfseService.editarNfse(payload).subscribe({
          next: async () => {
            this._snackBar.open("Registro Salvo com Sucesso");
            await this.buscaNfse(this.nfseCadastroForm.value.nfseId);
            resolve();
          },
          error: (error: HttpErrorResponse) => {
            this.dialog.open(InfoDialogComponent, {
              data: {
                titulo: "Erro",
                mensagem: this.getErrorMessage(error)
              }
            });
            reject(error);
          }
        });

      // Endpoint antigo:
      // this.nfseService.putNfse(this.nfseCadastroForm.value, this.nfseCadastroForm.value.nfseId).subscribe({
      //   next: async () => {
      //     this._snackBar.open("Registro Salvo com Sucesso");
      //     await this.buscaNfse(this.nfseCadastroForm.value.nfseId);
      //     resolve();
      //   },
      //   error: (error: HttpErrorResponse) => {
      //     this.dialog.open(InfoDialogComponent, {
      //       data: {
      //         titulo: "Erro",
      //         mensagem: this.getErrorMessage(error)
      //       }
      //     });
      //     reject(error);
      //   }
      // });
      });
    }
  }




  async salvarNovaArquitetura(): Promise<void> {
    return new Promise((resolve, reject) => {
      const formValue = this.nfseCadastroForm.value;

    const nfseCriarDto = {
      empresaId: formValue.empresaId,
      clienteId: formValue.clienteId,
      faturaId: formValue.faturaId || 0,
      listaServico: formValue.listaServico,
      discriminacao: formValue.discriminacao,
      municipioPrestacaoId: formValue.localPrestacaoMunicipioId,
      cnae: formValue.cnae || '',
      codArt: formValue.codArt || '',
      codObra: formValue.codObra || '',
      tipoDps: formValue.tipoRps || 1,
      outrasRetencoes: formValue.outrasRetencoes || 0,
      valorDeducoes: formValue.valorDeducoes || 0,
      descontoIncondicionado: formValue.descontoIncondicionado || 0,
      descontoCondicionado: formValue.descontoCondicionado || 0,
      valorCalcReeRepRes: formValue.valorCalcReeRepRes || 0,
      isOperacaoUsoPessoal: formValue.isOperacaoUsoPessoal,
      tipoOperacaoCompraGovOuImoveis: formValue.tipoOperacaoCompraGovOuImoveis,
      chaveNfseReferenciadaCompraGovOuImoveis: formValue.chaveNfseReferenciadaCompraGovOuImoveis,
      aliquotaImpostoId: formValue.aliquotaImpostoId,
      nfseItens: [{
        descricao: formValue.discriminacao,
        quantidade: 1,
        valorUnitario: formValue.valorServicos
      }]
    };

      this.nfseService.criarNfse(nfseCriarDto).subscribe({
        next: async (data) => {

          this.INfse.nfseId = data.body.nfseId;
          this._snackBar.open("NFS-e criada com sucesso! Impostos calculados automaticamente.");
          await this.buscaNfse(this.INfse.nfseId);
          resolve();
        },
        error: (error: HttpErrorResponse) => {
          this.dialog.open(InfoDialogComponent, {
            data: {
              titulo: "Erro ao criar NFS-e",
              mensagem: this.getErrorMessage(error)
            }
          });
          reject(error);
        }
      });
    });
  }


async enviarSefaz() {
  this.isDisabled = true;

  try {
    await this.salvar(); // Aguarda salvar terminar

      const data30DiasAtras = new Date(new Date().setDate(new Date().getDate() - 30));
      const dataEmissao = new Date(this.nfseCadastroForm.value.dtEmissao);

      if (dataEmissao < data30DiasAtras) {
        this.dialog.open(InfoDialogComponent, {
          data: {
            titulo: "Erro",
            mensagem: "Não é possível emitir NFS-e com competência superior a 30 dias atrás"
          }
        });
        return;
      }
      this.nfseCadastroForm.get('statusProcessamento').setValue('105');
      this.nfseService.postProcessaNfse(this.nfseCadastroForm.value, this.nfseCadastroForm.value.nfseId).subscribe(
        (data: { body: Nfse }) => {

          const nfse = data.body;

          this.insereDadosNfse(nfse);

          if (nfse.statusProcessamento == "100") {
            this.dialog.open(InfoDialogComponent, {
              data: {
                titulo: "Sucesso",
                mensagem: "NFS-e Autorizada"
              }
            });
            this.isDisabled = true;
            //this.buscaNfse(this.nfseCadastroForm.value.nfseId);

        } else if (nfse.statusProcessamento == "1" || nfse.statusProcessamento == "2" || nfse.statusProcessamento == "A02" || nfse.statusProcessamento == "E4" || nfse.statusProcessamento == "CE95" || nfse.statusProcessamento == "105") {

            this.dialog.open(InfoDialogComponent, {
              data: {
                titulo: "Processando...",
                mensagem: "O sistema da prefeitura está processando sua Nota Fiscal."
              }
            }).afterClosed().subscribe(() => {
              this.isDisabled = true;
              this.insereDadosNfse(nfse);
              //this.buscaNfse(this.nfseCadastroForm.value.nfseId);
            });

          } else {
            this.dialog.open(InfoDialogComponent, {
              data: {
                titulo: "Erro " + nfse.statusProcessamento,
                mensagem: "Erro ao autorizar o NFS-e. Verifique o Log."
              }
            }).afterClosed().subscribe(() => {
              this.modalNfseStatus();
            });
            this.isDisabled = false;
            this.insereDadosNfse(nfse);
          }
        },
        (error: HttpErrorResponse) => {
          this.dialog.open(InfoDialogComponent, {
            data: {
              titulo: "Erro",
              mensagem: "Erro ao autorizar o NFS-e. Verifique o Log."
            }
          }).afterClosed().subscribe(() => {
            this.modalNfseStatus();
          });
          this.isDisabled = false;
        }
      );
    } catch (e) {
      // Se salvar der erro, cai aqui
      this.isDisabled = false;
      console.error("Erro ao salvar", e);
    }
  }


  verificarRetornoTardio() {

    let item: any = {}; // inicializa o objeto vazio

    item.protocolo = this.nfseCadastroForm.value.protocolo;
    item.tipoConsulta = 0;
    item.primeiraExecucao = this.nfseCadastroForm.value.dataEmissaoNfse;
    item.listaNfsesIds = [this.nfseCadastroForm.value.nfseId];


    this.nfseService.retornoTardio(item).subscribe((data: { body: Nfse[] }) => {

      const nfse = data.body[0];

      this.insereDadosNfse(nfse);

      if (nfse.statusProcessamento == "100") {
        this.dialog.open(InfoDialogComponent, {
          data: {
            titulo: "Sucesso",
            mensagem: "NFS-e Autorizada"
          }
        });
        this.isDisabled = true;
        //this.buscaNfse(this.nfseCadastroForm.value.nfseId);

        } else if (nfse.statusProcessamento == "1" || nfse.statusProcessamento == "2" || nfse.statusProcessamento == "A02" || nfse.statusProcessamento == "E4" || nfse.statusProcessamento == "CE95" || nfse.statusProcessamento == "105") {

        this.dialog.open(InfoDialogComponent, {
          data: {
            titulo: "Processando...",
            mensagem: "O sistema da prefeitura está processando sua Nota Fiscal."
          }
        }).afterClosed().subscribe(() => {
          this.isDisabled = true;
          this.insereDadosNfse(nfse);
          //this.buscaNfse(this.nfseCadastroForm.value.nfseId);
        });

      } else {
        this.dialog.open(InfoDialogComponent, {
          data: {
            titulo: "Erro " + data.body,
            mensagem: "Erro ao autorizar o NFS-e. Verifique o Log."
          }
        }).afterClosed().subscribe(() => {
          this.modalNfseStatus();
        });
        this.isDisabled = false;
        this.insereDadosNfse(nfse);
      }
    }),
      (error: HttpErrorResponse) => {
        this.dialog.open(InfoDialogComponent, {
          data: {
            titulo: "Erro",
            mensagem: "Erro ao autorizar o NFS-e. Verifique o Log."
          }
        }).afterClosed().subscribe(() => {
          this.modalNfseStatus();
        });
        this.isDisabled = false;
      }
  }

  modalNfseStatus(): void {

    const dialogRef = this.dialog.open(NfseStatusComponent, {
      data: {
        nfseId: this.nfseCadastroForm.value.nfseId
      },
      maxHeight: '100vh'
    });

    dialogRef.afterClosed().subscribe(result => {
      this.buscaNfse(this.nfseCadastroForm.value.nfseId);
    });
  }

  corstatusProcessamento(statusProcessamento) {
    let cor = "Erro";
    if (statusProcessamento == 100 || statusProcessamento == 101 || statusProcessamento == 102 || statusProcessamento == 135 || statusProcessamento == 95) {
      cor = "Sucesso";
    };
    if (statusProcessamento == 0 || statusProcessamento == 1 || statusProcessamento == 105) {
      cor = "Processando";
    };

    if (statusProcessamento == undefined) {
      cor = "Processando";
    };

    return cor;
  }

  cancelarNfse() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        titulo: "Confirmação",
        mensagem: "Deseja realmente cancelar esse NFS-e?"
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {

        this.nfseService.postCancelar(this.nfseCadastroForm.value.nfseId).subscribe(data => {

          if (data.body == 101 || data.body == 102) {

            const dialogRef = this.dialog.open(InfoDialogComponent, {
              data: {
                titulo: "Sucesso",
                mensagem: "NFS-e cancelada com sucesso."
              }
            });
          } else if (data.body == 90){
            const dialogRef = this.dialog.open(InfoDialogComponent, {
              data: {
                titulo: "Em processo de cancelamento",
                mensagem: "O pedido de cancelamento está em análise e processamento."
              }
            });

          }
          else {
            const dialogRef = this.dialog.open(InfoDialogComponent, {
              data: {
                titulo: "Erro",
                mensagem: "Erro ao cancelar o NFS-e. Verifique o Log."
              }
            });
          }

          this.buscaNfse(this.nfseCadastroForm.value.nfseId);

        }, (error: HttpErrorResponse) => {

          const dialogRef = this.dialog.open(InfoDialogComponent, {
            data: {
              titulo: "Erro",
              erro: error.error
            }
          });
        });

      }
    });
  }

  atualizaStatus(nfseTemp) {

    switch (nfseTemp.statusProcessamento) {
      case "100":
        this.statusNfse = "Autorizada";
        break;
      case null:
      case "":
      case "0":
        this.statusNfse = "Em processamento";
        break;
      case "1":
        this.statusNfse = "Envio Pendente";
        break;
      case "105":
      case "2":
        this.statusNfse = "Em processamento";
        break;
      case "90":
        this.statusNfse = "Em cancelamento";
      break;
      case "135":
      case "101":
        this.statusNfse = "Cancelada";
        break;
      default:
        this.statusNfse = "com Erro, verifique o status";
    }
  }

  calculaImpostos() {
    if (this.nfseCadastroForm.value.valorServicos > 0 && this.nfseCadastroForm.value.listaServico != undefined && this.nfseCadastroForm.value.listaServico != null && this.nfseCadastroForm.value.listaServico != '')

      this.nfseCadastroForm.get('discriminacao')?.clearValidators();
    this.nfseCadastroForm.updateValueAndValidity();

      const { issRetido, tipoRps, ...rest } = this.nfseCadastroForm.value;

        const payload = {
          ...rest,
          isIssqnRetido: issRetido === 1,
          tipoDps: tipoRps || 1,
          nfseItens: [{
            descricao: rest.discriminacao,
            quantidade: 1,
            valorUnitario: rest.valorServicos
          }]
        };

      this.nfseService.editarNfse(payload).subscribe(data => {

      this.nfseCadastroForm.get('discriminacao')?.setValidators(Validators.required);

      this.insereDadosNfse(data.body);

      }, (error: HttpErrorResponse) => {
        this.dialog.open(InfoDialogComponent, {
          data: {
            titulo: "Erro",
            mensagem: this.getErrorMessage(error)
          }
        });
      });
  }

  geraLinkImpressao() {
    this.nfseService.postGeraLinkImpressao(this.nfseCadastroForm.value).subscribe(response => {
      const contentType = response.headers.get('Content-Type');

      if (contentType === 'application/pdf') {
        //É um arquivo PDF, faz o download
        const blob = new Blob([response.body], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `nfse_${this.nfseCadastroForm.value.numeroNfse}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {

        const arrayBuffer = response.body as ArrayBuffer;
        const text = new TextDecoder().decode(arrayBuffer);
        console.log('Conteúdo recebido:', text);



          if (text.startsWith('http')) {
            window.open(text, '_blank');
          } else {
            window.open(`http://${text}`, '_blank');
          }

      }

    }, (error: HttpErrorResponse) => {
      this.dialog.open(InfoDialogComponent, {
        data: {
          titulo: "Erro",
          mensagem: error.error
        }
      });
    });
  }

  buscarTemplates() {

    let pesquisa: any = {};

    pesquisa.empresasId = [this.nfseCadastroForm.get("empresaId").value]
    pesquisa.tpTemplate = TipoTemplate.Nfse;
    pesquisa.ativos = true;


          this.nfseService.getVerificaSePossuiTemplate(pesquisa).subscribe(data => {

              this.possuiTemplate = data.body;


          });

      }


  impressaoRPS(id: any) {

      this.nfseService.getImpressaoRPS(id).subscribe(response => {
          const text = response.body;
            this.htmlPrinterService.imprimirConteudoTinyMCE(text);

      })



  }


  calculavalorLiquido() {

    this.nfseCadastroForm.value.valorLiquidoNfse = this.nfseCadastroForm.value.valorServicos - this.nfseCadastroForm.value.valorPis - this.nfseCadastroForm.value.valorCofins - this.nfseCadastroForm.value.valorInss - this.nfseCadastroForm.value.valorIr - this.nfseCadastroForm.value.valorCsll - this.nfseCadastroForm.value.outrasRetencoes - this.nfseCadastroForm.value.valorIssRetido - this.nfseCadastroForm.value.descontoIncondicionado - this.nfseCadastroForm.value.descontoCondicionado;

    this.nfseCadastroForm.value.valorLiquidoNfse = Number((this.nfseCadastroForm.value.valorLiquidoNfse).toFixed(2));

  }

  determinaCliente(cliente: any) {

    this.clienteSelecionado = cliente;
    this.nfseCadastroForm.get('clienteId').setValue(cliente.clienteId);
    this.sugerirOperacaoUsoPessoal(cliente);
    this.limparCamposCompraGovernamentalSeNaoAplicavel();

    // Se o cliente possui textoNfse preenchido, adiciona ao campo discriminacao.
    try {
      const texto = (cliente?.textoNfse || '').toString().trim();
      if (texto) {
        const discControl = this.nfseCadastroForm.get('discriminacao');
        const atual = (discControl?.value || '').toString();
        // Evita duplicar o mesmo texto caso já esteja presente
        if (!atual.includes(texto)) {
          const novo = atual ? `${atual}\n${texto}` : texto;
          discControl?.setValue(novo);
        }
      }
    } catch (e) {
      console.warn('Erro ao anexar textoNfse na discriminacao', e);
    }
  }

  buscaCnaePorServico(listaServico: string): void {
    if (listaServico) {
      this.nfseService.getCnaeByListaServico(listaServico).subscribe(
        (response) => {

          if(response.body!="N/A"){

            this.cnae = response.body;
            this.nfseCadastroForm.get('cnae')?.setValidators(Validators.required);

            this.nfseCadastroForm.get('cnae')?.setValue(this.cnae);

            this.nfseCadastroForm.updateValueAndValidity();
          }

        }
      );
    }
  }

  deveExibirAbaCompraGovernamental(): boolean {
    return Number(this.clienteSelecionado?.codRegimeTributario || 0) > 4;
  }

  deveExibirChaveNfseReferenciadaCompraGovOuImoveis(): boolean {
    const tipoOperacao = Number(this.nfseCadastroForm.get('tipoOperacaoCompraGovOuImoveis')?.value);
    return tipoOperacao === 2 || tipoOperacao === 3;
  }


  private configurarCamposCompraGovernamental(): void {
    this.nfseCadastroForm.get('tipoOperacaoCompraGovOuImoveis')?.valueChanges.subscribe(() => {
      this.atualizarValidacaoChaveNfseReferenciadaCompraGovOuImoveis(true);
    });

    this.atualizarValidacaoChaveNfseReferenciadaCompraGovOuImoveis();
  }

  private atualizarValidacaoChaveNfseReferenciadaCompraGovOuImoveis(limparQuandoNaoAplicavel: boolean = false): void {
    const chaveControl = this.nfseCadastroForm.get('chaveNfseReferenciadaCompraGovOuImoveis');

    if (!chaveControl) {
      return;
    }

    if (this.deveExibirChaveNfseReferenciadaCompraGovOuImoveis()) {
      chaveControl.setValidators([Validators.required, Validators.maxLength(300)]);
    } else {
      chaveControl.clearValidators();

      if (limparQuandoNaoAplicavel) {
        chaveControl.setValue('');
      }
    }

    chaveControl.updateValueAndValidity({ emitEvent: false });
  }

  private sugerirOperacaoUsoPessoal(cliente: any): void {
    if (!cliente) {
      return;
    }

    const possuiCnpj = this.possuiDocumento(cliente.cnpj);
    const possuiCpf = this.possuiDocumento(cliente.cpf);

    if (possuiCnpj) {
      this.nfseCadastroForm.get('isOperacaoUsoPessoal')?.setValue(false);
      return;
    }

    if (possuiCpf || !possuiCnpj) {
      this.nfseCadastroForm.get('isOperacaoUsoPessoal')?.setValue(true);
    }
  }

  private possuiDocumento(documento: string | null | undefined): boolean {
    return !!documento && documento.toString().replace(/\D/g, '').length > 0;
  }

  private limparCamposCompraGovernamentalSeNaoAplicavel(): void {
    if (this.deveExibirAbaCompraGovernamental()) {
      this.atualizarValidacaoChaveNfseReferenciadaCompraGovOuImoveis();
      return;
    }

    this.nfseCadastroForm.patchValue({
      tipoOperacaoCompraGovOuImoveis: null,
      chaveNfseReferenciadaCompraGovOuImoveis: ''
    }, { emitEvent: false });

    this.atualizarValidacaoChaveNfseReferenciadaCompraGovOuImoveis();
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error.error && typeof error.error === 'string') {
      return error.error; // Mensagem já é uma string
    }
    if (typeof error.error.errors === 'object') {
      // `errors` é um objeto, converte em string legível
      return Object.values(error.error.errors).join('\n');
    }
    return "Ocorreu um erro inesperado. Tente novamente.";
  }

  onAliquotaImpostoChange(aliquotaImpostoId: number) {
    this.atualizarAliquotaImpostoSelecionada(aliquotaImpostoId);
  }

  buscarAliquotasImpostoPorEmpresa(empresaId: number, manterSelecaoAtual: boolean) {
    if (!empresaId || empresaId <= 0) {
      this.aliquotasImposto = [];
      this.nfseCadastroForm.patchValue({ aliquotaImpostoId: 0 });
      this.atualizarAliquotaImpostoSelecionada(0);
      return;
    }

    const aliquotaImpostoIdAtual = manterSelecaoAtual
      ? this.nfseCadastroForm.get('aliquotaImpostoId')?.value
      : 0;

    if (!manterSelecaoAtual) {
      this.nfseCadastroForm.patchValue({ aliquotaImpostoId: 0 });
      this.atualizarAliquotaImpostoSelecionada(0);
    }

    this.carregandoAliquotasImposto = true;

    this.aliquotaImpostoService.getAliquotasPorEmpresa(empresaId)
      .pipe(finalize(() => this.carregandoAliquotasImposto = false))
      .subscribe(data => {
        this.aliquotasImposto = data.body || [];

        const aliquotaExisteNaLista = this.aliquotasImposto.some(x => x.aliquotaImpostoId === aliquotaImpostoIdAtual);

        if (aliquotaExisteNaLista) {
          this.nfseCadastroForm.patchValue({ aliquotaImpostoId: aliquotaImpostoIdAtual });
          this.atualizarAliquotaImpostoSelecionada(aliquotaImpostoIdAtual);
        }
        else {
          this.nfseCadastroForm.patchValue({ aliquotaImpostoId: this.aliquotasImposto[0]?.aliquotaImpostoId || 0 });
          this.atualizarAliquotaImpostoSelecionada(this.aliquotasImposto[0]?.aliquotaImpostoId || 0);
        }
      });
  }

  atualizarAliquotaImpostoSelecionada(aliquotaImpostoId: number) {
    this.aliquotaImpostoSelecionada = this.aliquotasImposto.find(x => x.aliquotaImpostoId === aliquotaImpostoId) || null;
  }

  private obterAliquotaImpostoSelecionada(): AliquotaImpostoDto | null {
    const aliquotaImpostoId = this.nfseCadastroForm.get('aliquotaImpostoId')?.value;

    if (!(aliquotaImpostoId > 0)) {
      return null;
    }

    return this.aliquotasImposto.find(x => x.aliquotaImpostoId === aliquotaImpostoId)
      || this.aliquotaImpostoSelecionada
      || null;
  }

  private criarPayloadNfseParaPersistencia() {
    return {
      ...this.nfseCadastroForm.value
    };
  }

  private truncarDescricao(descricao: string | undefined | null, tamanhoMaximo: number = 28): string {
    if (!descricao) {
      return 'Sem descrição';
    }

    const descricaoNormalizada = descricao.trim();

    if (descricaoNormalizada.length <= tamanhoMaximo) {
      return descricaoNormalizada;
    }

    return `${descricaoNormalizada.slice(0, tamanhoMaximo).trim()} (...)`;
  }

  private obterSituacaoTributariaCodigo(aliquotaImposto: AliquotaImpostoDto): string {
    return aliquotaImposto?.situacaoTributaria?.codigo
      ?? aliquotaImposto?.classificacaoTributaria?.situacaoTributaria?.codigo
      ?? 'Sem código situação';
  }

  private obterSituacaoTributariaDescricao(aliquotaImposto: AliquotaImpostoDto): string {
    return aliquotaImposto?.situacaoTributaria?.descricao
      ?? aliquotaImposto?.classificacaoTributaria?.situacaoTributaria?.descricao
      ?? 'Sem descrição da situação';
  }

  private obterClassificacaoTributariaCodigo(aliquotaImposto: AliquotaImpostoDto): string {
    return aliquotaImposto?.classificacaoTributaria?.codigo ?? 'Sem código classificação';
  }

  private obterClassificacaoTributariaDescricao(aliquotaImposto: AliquotaImpostoDto): string {
    return aliquotaImposto?.classificacaoTributaria?.descricao ?? 'Sem descrição da classificação';
  }

  formatarAliquotaImpostoResumo(aliquotaImposto: AliquotaImpostoDto, tamanhoDescricaoNbs: number = 32, tamanhoDescricaoClassificacao: number = 32): string {
    const codigoNbs = aliquotaImposto?.nbs?.codigo ? aliquotaImposto.nbs.codigo : 'Sem código NBS';
    const descricaoNbs = this.truncarDescricao(aliquotaImposto?.nbs?.descricao, tamanhoDescricaoNbs);
    const codigoClassificacaoTributaria = this.obterClassificacaoTributariaCodigo(aliquotaImposto);
    const descricaoClassificacaoTributaria = this.truncarDescricao(this.obterClassificacaoTributariaDescricao(aliquotaImposto), tamanhoDescricaoClassificacao);

    return `NBS: ${codigoNbs} - ${descricaoNbs} | Classificação Tributária: ${codigoClassificacaoTributaria} - ${descricaoClassificacaoTributaria}`;
  }

  formatarAliquotaImpostoDetalhes(aliquotaImposto: AliquotaImpostoDto, tamanhoDescricaoListaServico: number = 28, tamanhoDescricaoSituacao: number = 28): string {
    const codigoServicoLista = aliquotaImposto?.servicoLista?.codigo ? aliquotaImposto.servicoLista.codigo : 'Sem código lista';
    const descricaoServicoLista = this.truncarDescricao(aliquotaImposto?.servicoLista?.descricao, tamanhoDescricaoListaServico);
    const codigoSituacaoTributaria = this.obterSituacaoTributariaCodigo(aliquotaImposto);
    const descricaoSituacaoTributaria = this.truncarDescricao(this.obterSituacaoTributariaDescricao(aliquotaImposto), tamanhoDescricaoSituacao);
    const codigoIndicador = aliquotaImposto?.codigoIndicadorOperacaoIBSCBS?.codigoIndicadorOperacao
      ? aliquotaImposto.codigoIndicadorOperacaoIBSCBS.codigoIndicadorOperacao
      : 'Sem código indicador';

    return `Lista de Serviço: ${codigoServicoLista} - ${descricaoServicoLista} | Situação Tributária: ${codigoSituacaoTributaria} - ${descricaoSituacaoTributaria} | Cód. Indicador: ${codigoIndicador}`;
  }

  formatarAliquotaImpostoSelecionada(aliquotaImposto: AliquotaImpostoDto): string {
    if (!aliquotaImposto) {
      return 'Selecione';
    }

    return this.formatarAliquotaImpostoResumo(aliquotaImposto);
  }
}
